import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { endpoints } from "@/api";
import { Button } from "@/components/ui/button";
import { Plus, Pencil, Trash2, ExternalLink } from "lucide-react";
import { useI18n } from "@/i18n";
import { toast } from "sonner";

export default function AdminBlogTab() {
  const { isRTL } = useI18n();
  const qc = useQueryClient();
  const { data: posts = [], isLoading } = useQuery({ queryKey: ["blog"], queryFn: endpoints.listBlogPosts });

  const onDelete = async (id) => {
    if (!window.confirm(isRTL ? "حذف هذا المقال؟" : "Delete this post?")) return;
    try {
      await endpoints.deleteBlogPost(id);
      toast.success(isRTL ? "تم الحذف" : "Deleted");
      qc.invalidateQueries({ queryKey: ["blog"] });
    } catch {
      toast.error(isRTL ? "تعذّر الحذف" : "Delete failed");
    }
  };

  if (isLoading) return <div className="text-[#5C544F]">…</div>;

  return (
    <div className="space-y-4" data-testid="admin-blog-tab">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-[#2A2421]">
          {isRTL ? `المقالات (${posts.length})` : `Posts (${posts.length})`}
        </h3>
        <Link to="/blog/new">
          <Button className="bg-[#C86240] hover:bg-[#b15637] text-white rounded-full" data-testid="admin-add-post">
            <Plus className="w-4 h-4 me-1" /> {isRTL ? "مقال جديد" : "New post"}
          </Button>
        </Link>
      </div>

      {posts.length === 0 && (
        <div className="text-center py-12 text-[#5C544F] bg-white rounded-2xl border border-[#2A2421]/10">
          {isRTL ? "لا توجد مقالات بعد" : "No posts yet"}
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {posts.map((p) => (
          <div key={p.id} className="rounded-2xl bg-white border border-[#2A2421]/10 overflow-hidden" data-testid={`admin-blog-post-${p.id}`}>
            {p.cover_image && (
              <img src={p.cover_image} alt="" className="w-full h-32 object-cover" />
            )}
            <div className="p-4 space-y-2">
              <div className="font-semibold text-[#2A2421] text-sm line-clamp-2">{p.title_en || p.title}</div>
              {p.title_ar && <div className="text-xs text-[#5C544F]" dir="rtl">{p.title_ar}</div>}
              <div className="text-[10px] uppercase tracking-widest text-[#C86240] font-semibold">/{p.slug}</div>
              <div className="flex items-center gap-2 pt-2 border-t border-[#2A2421]/8">
                <Link to={`/blog/${p.slug}`} target="_blank" rel="noopener noreferrer">
                  <Button size="sm" variant="ghost" className="rounded-full" data-testid={`admin-view-post-${p.id}`}>
                    <ExternalLink className="w-3.5 h-3.5" />
                  </Button>
                </Link>
                <Link to={`/blog/${p.id}/edit`}>
                  <Button size="sm" variant="outline" className="rounded-full" data-testid={`admin-edit-post-${p.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                </Link>
                <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50 rounded-full ms-auto" onClick={() => onDelete(p.id)} data-testid={`admin-delete-post-${p.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
